import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Box, CircularProgress, Alert, Typography, Paper } from '@mui/material';

import Breadcrumbs from '../../global/Components/Breadcrumbs';
import ReportToolbar from './components/ReportToolbar';

import PeriodActivityView from './views/PeriodActivityView';
import ActivityLogView from './views/ActivityLogView';
import CompanyPerformanceView from './views/CompanyPerformanceView';
import DeferredCostView from './views/DeferredCostView';

import {
    generateReport,
    getBusinessesForProject,
    getFloorplansForProject,
} from './services/reportsService';

import { REPORT_TYPES } from './constants';
import { formatDateTime, getTodayFormatted } from '../../utils/dateUtils';
import { usePermissions } from '../../../hooks/usePermissions';
import { canSeeDeferredCost } from '../../../utils/roles';

const getDefaultFilters = () => {
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    return {
        dateFrom: thirtyDaysAgo.toISOString().split('T')[0],
        dateTo: today.toISOString().split('T')[0],
        statuses: [],
        assignedToBusinessIds: [],
        floorplanIds: [],
        hasDeferredCost: 'ALL',
        includeInternalOnly: true,
        onlyInternallyCreated: false,
    };
};

const ReportsPage = () => {
    const { projectId } = useParams();
    const { getProjectRole } = usePermissions();
    // EXT-LAUNCH-009: only deferred-cost-permitted roles get the deferred-cost report type,
    // filter, and figures. The backend also redacts/blocks these for non-permitted roles.
    const showDeferredCost = canSeeDeferredCost(getProjectRole(projectId));

    const [reportType, setReportType] = useState('PERIOD_ACTIVITY');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [businesses, setBusinesses] = useState([]);
    const [floorplans, setFloorplans] = useState([]);
    const [filters, setFilters] = useState(getDefaultFilters);

    // Load filter options (businesses, floorplans) for the project
    const loadFilterOptions = useCallback(async () => {
        if (!projectId) return;

        try {
            const [businessData, floorplanData] = await Promise.all([
                getBusinessesForProject(projectId),
                getFloorplansForProject(projectId),
            ]);
            setBusinesses(businessData);
            setFloorplans(floorplanData);
        } catch (err) {
            // Filter options failed to load - not critical, user can still generate reports
            console.warn('Failed to load filter options:', err);
        }
    }, [projectId]);

    // Generate report with current filters
    const handleGenerateReport = useCallback(async () => {
        // Activity Summary and Deferred Cost are snapshots - no date range required
        const isSnapshotReport = reportType === 'PERIOD_ACTIVITY' || reportType === 'DEFERRED_COST';

        if (!isSnapshotReport && (!filters.dateFrom || !filters.dateTo)) {
            setError('Please select a date range');
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // For snapshot reports, exclude date range but keep other filters
            const reportFilters = isSnapshotReport
                ? {
                      ...filters,
                      dateFrom: undefined,
                      dateTo: undefined,
                  }
                : filters;

            const data = await generateReport(projectId, reportType, reportFilters, 0, 100);
            setReportData(data);
        } catch (err) {
            const errorMessage =
                err.response?.data ||
                err.response?.data?.message ||
                'Failed to generate report. Please try again.';
            setError(
                typeof errorMessage === 'string'
                    ? errorMessage
                    : 'Failed to generate report. Please try again.'
            );
        } finally {
            setLoading(false);
        }
    }, [projectId, reportType, filters]);

    // Load filter options on mount
    useEffect(() => {
        loadFilterOptions();
    }, [loadFilterOptions]);

    // Clear report data and reset filters when report type changes to show "Ready to Generate" state
    useEffect(() => {
        setReportData(null);
        setError(null);
        setFilters(getDefaultFilters());
    }, [reportType]);

    const handleActivityLogPageChange = async (newPage, newPageSize) => {
        try {
            setLoading(true);
            setError(null);

            const data = await generateReport(projectId, reportType, filters, newPage, newPageSize);
            setReportData(data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load page. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };

    const reportTypeLabel =
        REPORT_TYPES.find((type) => type.id === reportType)?.label || reportType.replace(/_/g, ' ');

    const handleDownloadPDF = async () => {
        // Lazy-load PDF generation libraries
        const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
            import('html2canvas'),
            import('jspdf'),
        ]);

        const contentElement = document.querySelector('.report-content');

        if (!contentElement) {
            console.error('Report content not found');
            return;
        }

        // Create a temporary wrapper for PDF
        const wrapper = document.createElement('div');
        wrapper.style.padding = '30px';
        wrapper.style.backgroundColor = 'white';
        wrapper.style.fontFamily = 'Arial, sans-serif';
        wrapper.style.width = '100%';

        // Create header manually (since the original has no-print class)
        const pdfHeader = document.createElement('div');
        pdfHeader.style.marginBottom = '25px';
        pdfHeader.style.paddingBottom = '15px';
        pdfHeader.style.borderBottom = '2px solid #333';

        // Report title
        const title = document.createElement('h1');
        title.textContent = reportTypeLabel;
        title.style.fontSize = '24px';
        title.style.fontWeight = '700';
        title.style.margin = '0 0 10px 0';
        title.style.color = '#1a1a1a';
        pdfHeader.appendChild(title);

        // Project name and date range (show "Current Snapshot" for snapshot reports)
        const subtitle = document.createElement('div');
        subtitle.style.fontSize = '14px';
        subtitle.style.color = '#666';
        subtitle.style.marginBottom = '5px';
        const isSnapshotReport = reportType === 'PERIOD_ACTIVITY' || reportType === 'DEFERRED_COST';
        subtitle.textContent = isSnapshotReport
            ? `${reportData?.projectName || 'Project Report'} • Current Snapshot`
            : `${reportData?.projectName || 'Project Report'} • ${filters.dateFrom} — ${filters.dateTo}`;
        pdfHeader.appendChild(subtitle);

        // Generated timestamp
        if (reportData?.generatedAt) {
            const timestamp = document.createElement('div');
            timestamp.style.fontSize = '11px';
            timestamp.style.color = '#999';
            timestamp.textContent = `Generated on ${formatDateTime(reportData.generatedAt)}`;
            pdfHeader.appendChild(timestamp);
        }

        wrapper.appendChild(pdfHeader);

        // Clone and append content
        const contentClone = contentElement.cloneNode(true);

        // Fix status chips to show text in PDF
        contentClone.querySelectorAll('.MuiChip-root').forEach((chip) => {
            // Get the background color from computed styles
            const bgColor = window.getComputedStyle(chip).backgroundColor;

            // Find the label element
            const label = chip.querySelector('.MuiChip-label');
            const labelText = label ? label.textContent.trim() : '';

            if (labelText) {
                // Replace chip with a simple styled span for PDF
                const simpleChip = document.createElement('span');
                simpleChip.textContent = labelText;
                simpleChip.style.backgroundColor = bgColor || '#777';
                simpleChip.style.color = 'white';
                simpleChip.style.fontWeight = '600';
                simpleChip.style.padding = '4px 12px';
                simpleChip.style.borderRadius = '4px';
                simpleChip.style.display = 'inline-block';
                simpleChip.style.fontSize = '11px';
                simpleChip.style.whiteSpace = 'nowrap';
                simpleChip.style.lineHeight = '1.5';

                // Replace the MUI chip with our simple version
                chip.parentNode.replaceChild(simpleChip, chip);
            }
        });

        // Optimize tables for PDF
        contentClone.querySelectorAll('table').forEach((table) => {
            table.style.width = '100%';
            table.style.fontSize = '10px';
            table.style.borderCollapse = 'collapse';

            // Style table cells
            table.querySelectorAll('th, td').forEach((cell) => {
                cell.style.padding = '8px 6px';
                cell.style.fontSize = '10px';
                cell.style.whiteSpace = 'normal';
                cell.style.wordWrap = 'break-word';
            });
        });

        // Remove pagination controls
        contentClone.querySelectorAll('.MuiTablePagination-root').forEach((el) => el.remove());

        wrapper.appendChild(contentClone);

        // Temporarily append to body
        document.body.appendChild(wrapper);

        // Generate filename
        const reportTypeNames = {
            PERIOD_ACTIVITY: 'Activity_Summary',
            ACTIVITY_LOG: 'Activity_Log',
            COMPANY_PERFORMANCE: 'Company_Performance',
            DEFERRED_COST: 'Deferred_Cost',
        };
        const filename = `${reportTypeNames[reportType] || 'Report'}_${getTodayFormatted()}.pdf`;

        // Generate PDF using html2canvas and jsPDF
        html2canvas(wrapper, {
            scale: 2,
            useCORS: true,
            letterRendering: true,
            logging: false,
            backgroundColor: '#ffffff',
        })
            .then((canvas) => {
                // Create PDF in landscape orientation for better table fit
                const pdf = new jsPDF({
                    orientation: 'landscape',
                    unit: 'in',
                    format: 'letter',
                    compress: true,
                });

                const imgWidth = 11; // Letter width in landscape (11 inches)
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                const pageHeight = 8.5; // Letter height in landscape (8.5 inches)

                let heightLeft = imgHeight;
                let position = 0;

                // Add first page
                const imgData = canvas.toDataURL('image/jpeg', 0.95);
                pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;

                // Add additional pages if content is longer than one page
                while (heightLeft > 0) {
                    position = heightLeft - imgHeight;
                    pdf.addPage();
                    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
                    heightLeft -= pageHeight;
                }

                // Save the PDF
                pdf.save(filename);

                // Cleanup
                document.body.removeChild(wrapper);
            })
            .catch((error) => {
                console.error('PDF generation error:', error);
                document.body.removeChild(wrapper);
            });
    };

    const renderReportView = () => {
        if (!reportData) return null;

        switch (reportType) {
            case 'PERIOD_ACTIVITY':
                return (
                    <PeriodActivityView
                        reportData={reportData}
                        filters={filters}
                        showDeferredCost={showDeferredCost}
                    />
                );
            case 'ACTIVITY_LOG':
                return (
                    <ActivityLogView
                        reportData={reportData}
                        onPageChange={handleActivityLogPageChange}
                    />
                );
            case 'COMPANY_PERFORMANCE':
                return <CompanyPerformanceView reportData={reportData} projectId={projectId} />;
            case 'DEFERRED_COST':
                return <DeferredCostView reportData={reportData} />;
            default:
                return null;
        }
    };

    return (
        <>
            <style>
                {`
                    /* Ensure status chip text is visible in PDFs */
                    .report-content .MuiChip-root {
                        -webkit-print-color-adjust: exact !important;
                        print-color-adjust: exact !important;
                        color-adjust: exact !important;
                    }
                    .report-content .MuiChip-label {
                        color: white !important;
                        font-weight: 600 !important;
                    }
                    
                    /* Page break helpers for PDF */
                    .page-break {
                        page-break-before: always;
                    }
                    
                    /* Hide elements in print/PDF */
                    @media print {
                        .no-print {
                            display: none !important;
                        }
                    }
                `}
            </style>
            <Box
                sx={{
                    minHeight: '100vh',
                }}
            >
                <Box>
                    {/* Breadcrumbs */}
                    <Box className="no-print" sx={{ mb: 3 }}>
                        <Breadcrumbs
                            links={[
                                { label: 'Dashboard', path: `/projects/${projectId}` },
                                { label: 'Reports', path: `/projects/${projectId}/reports` },
                            ]}
                        />
                    </Box>

                    {/* Top Toolbar */}
                    <Box className="no-print">
                        <ReportToolbar
                            reportType={reportType}
                            onReportTypeChange={setReportType}
                            filters={filters}
                            onFilterChange={handleFilterChange}
                            onGenerate={handleGenerateReport}
                            loading={loading}
                            businesses={businesses}
                            floorplans={floorplans}
                            onDownloadPDF={handleDownloadPDF}
                            showDeferredCost={showDeferredCost}
                        />
                    </Box>

                    {/* Main Content Area */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Minimal Header */}
                        {reportData && (
                            <Box sx={{ px: 1 }}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 700,
                                        color: 'text.primary',
                                        textAlign: 'left',
                                    }}
                                >
                                    {reportTypeLabel}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ textAlign: 'left' }}
                                >
                                    {reportData.projectName}
                                </Typography>
                            </Box>
                        )}

                        {/* Error Alert */}

                        {/* Error Alert */}
                        {error && (
                            <Alert severity="error" onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        {/* Report Content */}
                        {loading ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: 400,
                                    gap: 2,
                                }}
                            >
                                <CircularProgress />
                                <Typography variant="body2" color="text.secondary">
                                    Generating report...
                                </Typography>
                            </Box>
                        ) : reportData ? (
                            <Box className="report-content" sx={{ p: 1 }}>
                                {renderReportView()}
                            </Box>
                        ) : (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 8,
                                    borderRadius: 2,
                                    border: '1px dashed',
                                    borderColor: 'divider',
                                    textAlign: 'center',
                                    bgcolor: 'transparent',
                                }}
                            >
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    Ready to Generate
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Configure your filters above and click &ldquo;Generate
                                    Report&rdquo; to view data.
                                </Typography>
                            </Paper>
                        )}
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default ReportsPage;
